import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { RevealText } from './Reveal';
import { useMotionMode } from '../../contexts/MotionContext';

export const PageHero = ({ image, eyebrow, title, subtitle, imageOpacity = 0.3, testId = 'page-hero' }) => {
  const ref = useRef(null);
  const { reduced } = useMotionMode();
  const k = reduced ? 0 : 1;
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', `${30 * k}%`]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.05 + 0.2 * k]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -140 * k]);
  const textOpacity = useTransform(scrollYProgress, (v) => (reduced ? 1 : 1 - Math.min(v / 0.8, 1)));

  return (
    <section ref={ref} className="relative py-28 lg:py-40 overflow-hidden" data-testid={testId}>
      <motion.div className="absolute inset-0 will-change-transform" style={{ y, scale, opacity: imageOpacity }}>
        <img src={image} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
      <div className="absolute inset-0 oc-grain pointer-events-none" />
      <motion.div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center" style={{ y: textY, opacity: textOpacity }}>
        {eyebrow && (
          <motion.p className="text-xs uppercase tracking-[0.35em] text-white/50 mb-6" initial={{ opacity: 0, letterSpacing: '0.1em' }} animate={{ opacity: 1, letterSpacing: '0.35em' }} transition={{ duration: 1.2 }}>
            {eyebrow}
          </motion.p>
        )}
        <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight"><RevealText text={title} animateOnMount delay={0.15} /></h1>
        {subtitle && (
          <motion.p className="text-lg md:text-2xl text-white/70 max-w-3xl mx-auto" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            {subtitle}
          </motion.p>
        )}
      </motion.div>
    </section>
  );
};

export const ParallaxImage = ({ src, alt, className = '' }) => {
  const ref = useRef(null);
  const { reduced } = useMotionMode();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], reduced ? ['0%', '0%'] : ['-10%', '10%']);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y, scale: 1.25 }} className="w-full h-full object-cover" />
    </div>
  );
};
